import { Injectable, Signal, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

export type ConfirmationKind = 'primary' | 'danger';
export type ConfirmationSize = 'default' | 'lg';

export interface ConfirmationModalConfig {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmKind?: ConfirmationKind;
  /** default ≈ 380px; lg ≈ 520px with roomier type/spacing */
  size?: ConfirmationSize;
  /** When false, hides the header X (use when Cancel/Later is enough). Default true. */
  showClose?: boolean;
  /**
   * When set, shows a free-text reason field in the modal.
   * Use {@link ConfirmationModalService.confirmWithReason} to read the value.
   */
  reasonInput?: {
    label?: string;
    placeholder?: string;
  };
}

export interface ConfirmationResult {
  confirmed: boolean;
  /** Trimmed reason when reasonInput was enabled; empty string if left blank. */
  reason: string;
}

interface ActiveConfirmation extends Required<
  Pick<ConfirmationModalConfig, 'confirmLabel' | 'cancelLabel' | 'confirmKind' | 'size' | 'showClose'>
> {
  title: string;
  message: string;
  reasonInput: ConfirmationModalConfig['reasonInput'] | null;
  decision$: Subject<ConfirmationResult>;
}

@Injectable({ providedIn: 'root' })
export class ConfirmationModalService {
  private readonly activeConfirmationSignal = signal<ActiveConfirmation | null>(null);

  readonly activeConfirmation: Signal<ActiveConfirmation | null> = this.activeConfirmationSignal.asReadonly();

  confirm(config: ConfirmationModalConfig): Observable<boolean> {
    return this.open(config).pipe(map((result) => result.confirmed));
  }

  /** Same as confirm, but returns the optional reason field value. */
  confirmWithReason(config: ConfirmationModalConfig): Observable<ConfirmationResult> {
    return this.open({
      ...config,
      reasonInput: config.reasonInput ?? {
        label: 'Reason (optional)',
        placeholder: 'Leave blank to use the default'
      }
    });
  }

  private open(config: ConfirmationModalConfig): Observable<ConfirmationResult> {
    const existing = this.activeConfirmationSignal();
    if (existing) {
      existing.decision$.next({ confirmed: false, reason: '' });
      existing.decision$.complete();
    }

    const decision$ = new Subject<ConfirmationResult>();
    this.activeConfirmationSignal.set({
      title: config.title,
      message: config.message,
      confirmLabel: config.confirmLabel ?? 'Confirm',
      cancelLabel: config.cancelLabel ?? 'Cancel',
      confirmKind: config.confirmKind ?? 'primary',
      size: config.size ?? 'default',
      showClose: config.showClose ?? true,
      reasonInput: config.reasonInput ?? null,
      decision$
    });

    return new Observable<ConfirmationResult>((subscriber) => {
      const sub = decision$.subscribe(subscriber);
      return () => sub.unsubscribe();
    });
  }

  resolve(decision: boolean, reason = ''): void {
    const active = this.activeConfirmationSignal();
    if (!active) return;

    active.decision$.next({
      confirmed: decision,
      reason: decision ? reason.trim() : ''
    });
    active.decision$.complete();
    this.activeConfirmationSignal.set(null);
  }
}
